import 'react-datepicker/dist/react-datepicker.css'
import React, { useEffect, useCallback, useState, useRef, useMemo } from 'react'
import { Alert, Button, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { registerLocale, setDefaultLocale } from 'react-datepicker'
import es from 'date-fns/locale/es'
import moment from 'moment-timezone'

import { useCurrentUser } from '../../hooks/useCurrentUser'
import useApi from '../../hooks/useApi'
import DefaultCharity from '../../components/Charity/DefaultCharity'
import Loading from '../../components/loading'
import CivWallet from '../../components/CivWallet/civ-wallet'
import SettingsLayout from '../../components/Settings/SettingsLayout'
import SettingsCard from '../../components/Settings/SettingsCard'
import SelectionCard from '../../components/Settings/SelectionCard'
import { sortCategoriesAlphabetically } from '../../lib/sort'
import { formatPhoneNumberForSave } from '../../lib/phone'
import { ensureAbsoluteUrl } from '../../utils/url'

import AccountFieldsCard from './settings/AccountFieldsCard'
import ProfileHeaderCard from './settings/ProfileHeaderCard'
import SocialMediaCard from './settings/SocialMediaCard'
import PasswordCard from './settings/PasswordCard'
import QuoteCard from './settings/QuoteCard'
import EmailVerifyModal from './settings/EmailVerifyModal'
import PasswordConfirmModal from './settings/PasswordConfirmModal'
import normalizeUser, { selectionPatch } from './settings/normalizeUser'
import {
  buildCauseOptions,
  buildContributionOptions,
  buildSkillOptions,
  currentLang,
} from './settings/options'

registerLocale('es', es)
setDefaultLocale('en')

export default function AccountEditPage() {
  const { i18n, t } = useTranslation()

  const { currentUser, locale, setCurrentUser, fetchUser, hasBetaFeature } =
    useCurrentUser()
  const {
    getCurrentUser,
    updateAccount,
    updatePassword,
    updateUser,
    updateUserInformation,
    updateEmailHeaders,
    requestAccountVerification,
    sendAccountVerificationCode,
    getShirtSizes,
    getSkills,
    getCommunityContributions,
    getEventCategories,
    usernameAvailable,
  } = useApi()

  const [confirmPassword, setConfirmPassword] = useState()
  const [hasUserChanged, setHasUserChanged] = useState(false)
  const [openPasswordConfirmationModal, setOpenPasswordConfirmationModal] =
    useState(false)
  const [emailPasswordConfirmation, setEmailPasswordConfirmation] = useState()

  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [newPassword, setNewPassword] = useState()
  const [user, setUser] = useState(() => currentUser)
  const [email, setEmail] = useState(() => currentUser.email)

  const [waitingForEmailVerification, setWaitingForEmailVerification] =
    useState(false)
  const [emailVerificationCode, setEmailVerificationCode] = useState('')
  const [emailVerificationError, setEmailVerificationError] = useState(false)
  const [emailVerificationMaxPerDay, setEmailVerificationMaxPerDay] =
    useState(false)
  const [emailVerificationMaxPerMonth, setEmailVerificationMaxPerMonth] =
    useState(false)
  const [shirtSizes, setShirtSizes] = useState()
  const nameRef = useRef(null)
  const [checkingNickname, setCheckingNickname] = useState(false)
  const [nicknameTaken, setNicknameTaken] = useState(false)
  const nicknameCheckTimer = useRef(null)
  const [hasSocialsChanged, setHasSocialsChanged] = useState(false)
  const [isSavingSocials, setIsSavingSocials] = useState(false)
  const [skillsList, setSkillsList] = useState([])
  const [communityContributionsList, setCommunityContributionsList] = useState(
    [],
  )
  const [categoriesList, setCategoriesList] = useState([])

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    try {
      const sizes = await getShirtSizes()
      setShirtSizes(sizes.data)
      const [skillsRes, communityContributionsRes, categoriesRes, response] =
        await Promise.all([
          getSkills(),
          getCommunityContributions(),
          getEventCategories(),
          getCurrentUser(),
        ])
      const skillsData = skillsRes?.data ?? skillsRes
      const ccData =
        communityContributionsRes?.data ?? communityContributionsRes
      const categoriesData = categoriesRes?.data ?? categoriesRes
      setSkillsList(Array.isArray(skillsData) ? skillsData : [])
      setCommunityContributionsList(Array.isArray(ccData) ? ccData : [])
      setCategoriesList(
        Array.isArray(categoriesData)
          ? sortCategoriesAlphabetically(categoriesData, locale)
          : [],
      )

      const { data } = response
      if (data) {
        setUser(normalizeUser(data))
        setLoadError(false)
      }
    } catch (err) {
      // 401 or network error: surface it rather than throwing, so an expired
      // session falls through to the login redirect instead of the error
      // overlay. Previously this block referenced `data` from the try scope,
      // which threw a ReferenceError and left the page stuck on the spinner.
      console.warn('[Account edit] fetchData failed:', err)
      setLoadError(true)
    } finally {
      setIsLoading(false)
    }
  }, [
    getCurrentUser,
    getSkills,
    getCommunityContributions,
    getEventCategories,
    getShirtSizes,
    locale,
  ])

  const onLocationChange = useCallback(async _location => {
    setUser(user => ({
      ...user,
      location: _location?.description,
    }))
    setHasUserChanged(true)
  }, [])

  const onSchoolChange = useCallback(async _location => {
    setUser(user => ({
      ...user,
      school: _location?.description ?? '',
    }))
    setHasUserChanged(true)
  }, [])

  const onChangeNickname = useCallback(
    async value => {
      setUser(user => ({
        ...user,
        nickname: value,
      }))
      setHasUserChanged(true)

      // Clear existing timer
      if (nicknameCheckTimer.current) {
        clearTimeout(nicknameCheckTimer.current)
      }

      setCheckingNickname(false)
      setNicknameTaken(false)

      const trimmedValue = value.trim()
      if (!trimmedValue || trimmedValue === currentUser?.nickname) {
        return
      }

      nicknameCheckTimer.current = setTimeout(async () => {
        setCheckingNickname(true)
        try {
          const response = await usernameAvailable({ username: trimmedValue })
          const available = !!response?.data?.available
          setNicknameTaken(!available)
        } catch (error) {
          setNicknameTaken(true)
        } finally {
          setCheckingNickname(false)
        }
      }, 500)
    },
    [currentUser?.nickname, usernameAvailable],
  )

  const onReturnUrl = () => {
    if (localStorage.getItem('return-url')) {
      const url = localStorage.getItem('return-url')
      localStorage.removeItem('return-url')
      window.location = url
    }
  }

  const onEmailVerifyRequest = useCallback(async () => {
    const response = await requestAccountVerification()
    if (response.status === 200) {
      setWaitingForEmailVerification(true)
      setEmailVerificationError(false)
      setEmailVerificationCode('')
    } else {
      const { data } = response.error.response
      if (data.base && data.base[0].includes('max_per_day')) {
        setEmailVerificationMaxPerDay(true)
      } else if (data.base && data.base[0].includes('max_per_month')) {
        setEmailVerificationMaxPerMonth(true)
      }
    }
  })

  const onEmailVerifyCode = useCallback(async () => {
    const response = await sendAccountVerificationCode(emailVerificationCode)
    if (response.status === 200) {
      setWaitingForEmailVerification(false)
      onReturnUrl()
      if (localStorage.getItem('verify-email-message')) {
        localStorage.removeItem('verify-email-message')
        setCurrentUser({ ...currentUser, email_verified_at: new Date() })
        window.location = '/charities'
      } else {
        fetchData()
      }
    } else {
      setEmailVerificationError(true)
    }
  }, [emailVerificationCode, currentUser])

  const onEmailVerifyCancel = useCallback(async () => {
    setWaitingForEmailVerification(false)
    localStorage.removeItem('verify-email-message')
    onReturnUrl()
  })

  const onSaveNewPassword = useCallback(
    async e => {
      setIsLoading(true)
      e.preventDefault()
      const response = await updatePassword({
        newPassword,
        confirmPassword,
      })
      if (response.errors) {
        // eslint-disable-next-line no-alert
        alert(response.errors?.full_messages?.[0])
      } else {
        setNewPassword('')
        setConfirmPassword('')
      }
      setIsLoading(false)
    },
    [user, newPassword, confirmPassword],
  )

  const onSave = useCallback(async () => {
    if (user.school?.trim() && !user.school_type) {
      alert(t('users.edit.school_type_required'))
      return
    }
    setIsLoading(true)
    const params = {
      birthday: user.birthday
        ? moment(user.birthday).format('YYYY-MM-DD 12:00')
        : undefined,
      gender: user.gender,
      shirt_size_id: user.shirt_size_id,
      language: user.language,
      location: user.location,
      marketing_opt_in: user.marketing_opt_in,
      media_attachment_id: user.media_attachment_id,
      name: user.name,
      phone: user.phone ? formatPhoneNumberForSave(user.phone) : '',
      work_title: user.work_title,
      work_place: user.work_place,
      retired: user.retired,
      school: user.school ?? '',
      school_type: user.school_type ?? '',
    }

    const formData = new FormData()
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined) {
        formData.append(key, params[key])
      }
    })

    const response = await updateAccount(formData)

    if (response.errors) {
      // eslint-disable-next-line no-alert
      alert(response.errors?.full_messages?.[0])
    } else {
      if (user.language) {
        i18n.changeLanguage(user.language)
        moment.locale(user.language || locale)
      }
      setHasUserChanged(false)
    }

    if (user.id) {
      try {
        // Skills / causes / contributions are deliberately absent: each picker
        // card persists its own field, so including them here could overwrite a
        // newer selection with stale local state.
        const userUpdateResponse = await updateUser(user.id, {
          user: {
            work_title: user.work_title ?? '',
            work_place: user.work_place ?? '',
            retired: !!user.retired,
            school: user.school ?? '',
            school_type: user.school_type ?? '',
          },
        })
        if (userUpdateResponse?.status !== 200) {
          console.warn(
            '[Account edit] updateUser (profile extras) non-200:',
            userUpdateResponse,
          )
        }
      } catch (err) {
        console.warn('[Account edit] updateUser (profile extras) failed:', err)
      }
    }

    if (email !== user.email) {
      const userInformationParams = {
        user: {
          email,
          password: emailPasswordConfirmation,
        },
      }
      const userInformationResponse = await updateUserInformation(
        user.id,
        userInformationParams,
      )
      if (
        userInformationResponse?.success === false &&
        !(userInformationResponse?.status === 200)
      ) {
        alert(
          userInformationResponse?.errors?.full_messages?.[0] ??
            t('users.edit.email_can_not_be_updated'),
        )
        setEmail(user.email)
        setOpenPasswordConfirmationModal(false)
        setEmailPasswordConfirmation('')
      } else {
        const newUser = {
          ...user,
          email: email,
          email_verified_at: null,
          uid: email,
        }
        setUser(newUser)
        setOpenPasswordConfirmationModal(false)
        setEmailPasswordConfirmation('')
        updateEmailHeaders(email)
      }
    }

    if (
      user.nickname &&
      user.nickname !== currentUser?.nickname &&
      !nicknameTaken
    ) {
      try {
        const nicknameResponse = await updateUser(user.id, {
          user: { nickname: user.nickname.trim() },
        })
        if (nicknameResponse.status === 200) {
          const updatedUser = {
            ...user,
            nickname: user.nickname.trim(),
          }
          setUser(updatedUser)
          setCurrentUser(updatedUser)
          setNicknameTaken(false)
          setCheckingNickname(false)
        } else {
          alert(
            t('users.edit.nickname_can_not_be_updated') ||
              'Username could not be updated',
          )
        }
      } catch (error) {
        alert(
          t('users.edit.nickname_can_not_be_updated') ||
            'Username could not be updated',
        )
      }
    }

    setIsLoading(false)
  }, [
    user,
    setCurrentUser,
    fetchData,
    emailPasswordConfirmation,
    setUser,
    updateEmailHeaders,
    user?.email,
    email,
    currentUser,
    nicknameTaken,
    updateUser,
    t,
  ])

  useEffect(() => {
    fetchData()
    if (nameRef?.current) {
      nameRef.current.focus()
      nameRef.current.blur()
    }
    if (
      localStorage.getItem('force-verify-email') ||
      localStorage.getItem('new-account')
    ) {
      localStorage.removeItem('force-verify-email')
      localStorage.removeItem('new-account')
      fetchUser().then(() => {
        if (!user?.email_verified_at) {
          onEmailVerifyRequest()
        }
      })
    }
  }, [])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (nicknameCheckTimer.current) {
        clearTimeout(nicknameCheckTimer.current)
      }
    }
  }, [])

  const tryToSave = () => {
    if (user.email === email) {
      onSave()
    } else {
      setOpenPasswordConfirmationModal(true)
    }
  }

  const onSaveSocials = useCallback(async () => {
    if (!user?.id) return
    setIsSavingSocials(true)
    const userParams = {
      facebook_link: ensureAbsoluteUrl(user.facebook_link) || '',
      linkedin_link: ensureAbsoluteUrl(user.linkedin_link) || '',
      instagram_link: ensureAbsoluteUrl(user.instagram_link) || '',
      youtube_link: ensureAbsoluteUrl(user.youtube_link) || '',
      tiktok_link: ensureAbsoluteUrl(user.tiktok_link) || '',
      website_link: ensureAbsoluteUrl(user.website_link) || '',
    }
    try {
      const response = await updateUser(user.id, { user: userParams })
      if (response?.errors) {
        // eslint-disable-next-line no-alert
        alert(response.errors?.full_messages?.[0])
      } else {
        setHasSocialsChanged(false)
      }
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(
        err?.response?.data?.errors?.full_messages?.[0] ??
          'Failed to save socials',
      )
    }
    setIsSavingSocials(false)
  }, [
    user?.id,
    user?.facebook_link,
    user?.linkedin_link,
    user?.instagram_link,
    user?.youtube_link,
    user?.tiktok_link,
    user?.website_link,
    updateUser,
  ])

  const imageUserOnChange = async mediaAttachment => {
    const formData = new FormData()
    formData.append('media_attachment_id', mediaAttachment.id)
    const response = await updateAccount(formData)
    if (response.errors) {
      // eslint-disable-next-line no-alert
      alert(response.errors?.full_messages?.[0])
    } else {
      setUser({ ...user, media_attachment: { ...mediaAttachment } })
      setHasUserChanged(true)
    }
  }

  const lang = currentLang(i18n.language)
  const skillOptions = useMemo(
    () => buildSkillOptions(skillsList, lang),
    [skillsList, lang],
  )
  const contributionOptions = useMemo(
    () => buildContributionOptions(communityContributionsList, lang),
    [communityContributionsList, lang],
  )
  const causeOptions = useMemo(
    () => buildCauseOptions(categoriesList, lang),
    [categoriesList, lang],
  )

  /**
   * Persist one selection list (skills / causes / contributions) on its own.
   * Each picker card saves independently, so this sends only its own field.
   * Updates optimistically and rolls back if the request fails, returning
   * false so the modal can stay open rather than closing over an error.
   */
  const saveSelection = useCallback(
    async (key, ids) => {
      if (!user?.id) return false
      const previous = user[key] ?? []
      setUser(prev => ({ ...prev, [key]: ids }))
      try {
        const response = await updateUser(user.id, { user: { [key]: ids } })
        if (response?.errors || (response?.status && response.status !== 200)) {
          setUser(prev => ({ ...prev, [key]: previous }))
          // eslint-disable-next-line no-alert
          alert(
            response?.errors?.full_messages?.[0] ??
              t('users.edit.save_failed', 'Could not save your changes'),
          )
          return false
        }
        // Merge only this selection's fields from the server so unsaved edits
        // in the other cards survive. selectionPatch keeps the id array in
        // step with the nested collection (skills, categories, ...).
        const patch = selectionPatch(response?.data, key, ids)
        setUser(prev => ({ ...prev, ...patch }))
        setCurrentUser(prev => ({ ...prev, ...patch }))
        return true
      } catch (err) {
        setUser(prev => ({ ...prev, [key]: previous }))
        // eslint-disable-next-line no-alert
        alert(
          err?.response?.data?.errors?.full_messages?.[0] ??
            t('users.edit.save_failed', 'Could not save your changes'),
        )
        return false
      }
    },
    [user, updateUser, setCurrentUser, t],
  )

  const saveDisabled =
    (!hasUserChanged && email === user.email) ||
    (user?.nickname &&
      user.nickname !== currentUser?.nickname &&
      (checkingNickname || nicknameTaken)) ||
    (user?.school?.trim() && !user?.school_type?.trim())

  const breadcrumbs = [
    { label: t('navigation_banner.home'), to: '/' },
    {
      label: currentUser?.name,
      to: currentUser?.nickname
        ? `/profile/${encodeURIComponent(currentUser.nickname)}`
        : '/profile',
    },
    { label: t('users.edit.profile_settings') },
  ]

  return (
    <>
      <Loading show={isLoading} />

      <SettingsLayout
        title={t('users.edit.profile_settings')}
        breadcrumbs={breadcrumbs}
      >
        {loadError && (
          <Alert variant="warning">
            {t(
              'users.edit.load_failed',
              'We could not load your settings. Refresh the page to try again.',
            )}
          </Alert>
        )}

        <ProfileHeaderCard user={user} onAvatarChange={imageUserOnChange} />

        <AccountFieldsCard
          user={user}
          setUser={setUser}
          setHasUserChanged={setHasUserChanged}
          email={email}
          setEmail={setEmail}
          locale={locale}
          shirtSizes={shirtSizes}
          nameRef={nameRef}
          currentUser={currentUser}
          checkingNickname={checkingNickname}
          nicknameTaken={nicknameTaken}
          emailVerificationError={emailVerificationError}
          onEmailVerifyRequest={onEmailVerifyRequest}
          onChangeNickname={onChangeNickname}
          onLocationChange={onLocationChange}
          onSchoolChange={onSchoolChange}
          onSave={tryToSave}
          saveDisabled={!!saveDisabled}
          isSaving={isLoading}
        />

        <div className="row g-4">
          <div className="col-md-6 d-flex">
            <div className="flex-fill">
              <QuoteCard user={user} setUser={setUser} />
            </div>
          </div>
          <div className="col-md-6 d-flex">
            <div className="flex-fill">
              <SettingsCard
                icon="hand-holding-heart"
                title={t('users.edit.set_default_charity.title', 'Charity')}
                description={t(
                  'users.edit.set_default_charity.charity_component_description',
                )}
              >
                <DefaultCharity bare />
              </SettingsCard>
            </div>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-md-6 d-flex">
            <div className="flex-fill">
              <SelectionCard
                icon="briefcase"
                title={t('users.edit.skills')}
                description={t('users.edit.select_up_to_three')}
                options={skillOptions}
                value={user?.skill_ids ?? []}
                onSave={ids => saveSelection('skill_ids', ids)}
                buttonLabel={t('users.edit.select_skills', 'Select Skills')}
              />
            </div>
          </div>
          <div className="col-md-6 d-flex">
            <div className="flex-fill">
              <SelectionCard
                icon="hand"
                title={t('users.edit.causes.title')}
                description={t('users.edit.select_up_to_three')}
                options={causeOptions}
                value={user?.category_ids ?? []}
                onSave={ids => saveSelection('category_ids', ids)}
                showIcons
                buttonLabel={t(
                  'users.edit.select_causes',
                  'Select Favorite Causes',
                )}
              />
            </div>
          </div>
        </div>

        <SelectionCard
          icon="hands-holding-child"
          title={t('users.edit.community_contributions')}
          description={t('users.edit.select_up_to_three')}
          options={contributionOptions}
          value={user?.community_contribution_ids ?? []}
          onSave={ids => saveSelection('community_contribution_ids', ids)}
          buttonLabel={t(
            'users.edit.select_contributions',
            'Select Contributions',
          )}
        />

        <SocialMediaCard
          data={user}
          onFieldChange={(key, value) => {
            setUser({ ...user, [key]: value })
            setHasSocialsChanged(true)
          }}
          onSave={onSaveSocials}
          hasChanged={hasSocialsChanged}
          isSaving={isSavingSocials}
        />

        {hasBetaFeature('civ_wallet') ? (
          <SettingsCard
            id="wallet"
            icon="wallet"
            title={t('navigation_banner.civ_wallet', 'CIV Wallet')}
          >
            <CivWallet
              data={user}
              setData={setUser}
              locale={locale}
              updateData={updateUser}
              origin={'user'}
            />
          </SettingsCard>
        ) : null}

        <PasswordCard
          newPassword={newPassword}
          setNewPassword={setNewPassword}
          confirmPassword={confirmPassword}
          setConfirmPassword={setConfirmPassword}
          onSaveNewPassword={onSaveNewPassword}
        />
      </SettingsLayout>

      <EmailVerifyModal
        show={waitingForEmailVerification}
        onHide={onEmailVerifyCancel}
        email={user.email}
        error={emailVerificationError}
        onDismissError={() => setEmailVerificationError(false)}
        code={emailVerificationCode}
        setCode={setEmailVerificationCode}
        onVerify={onEmailVerifyCode}
      />

      <PasswordConfirmModal
        show={openPasswordConfirmationModal}
        onHide={() => setOpenPasswordConfirmationModal(false)}
        value={emailPasswordConfirmation}
        onChange={val => setEmailPasswordConfirmation(val)}
        onSave={onSave}
      />

      <Modal
        show={emailVerificationMaxPerDay}
        onHide={() => setEmailVerificationMaxPerDay(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('users.edit.email_verify_title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('users.edit.email_verify_max_day')}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setEmailVerificationMaxPerDay(false)}
          >
            {t('event_card.close')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={emailVerificationMaxPerMonth}
        onHide={() => setEmailVerificationMaxPerMonth(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('users.edit.email_verify_title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('users.edit.email_verify_max_month')}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setEmailVerificationMaxPerMonth(false)}
          >
            {t('event_card.close')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
